// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import {
  DefaultButton,
  Dropdown,
  initializeIcons,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Stack,
  Text,
  TextField
} from '@fluentui/react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import TopNav from '../components/TopNav';

initializeIcons();

type RoleRecord = {
  id: number;
  name: string;
  systemPrompt: string;
  createdAt: string;
};

type RolesProps = {
  userEmail: string;
};

const Roles = ({ userEmail }: RolesProps): JSX.Element => {
  const location = useLocation();
  const authProvider = useMemo(() => (location.pathname.startsWith('/app/v2') ? 'login' : 'sso'), [location.pathname]);
  const [roles, setRoles] = useState<RoleRecord[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState<string | null>(null);

  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createPrompt, setCreatePrompt] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);

  const [selectedRoleName, setSelectedRoleName] = useState<string>('');
  const [editPrompt, setEditPrompt] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [editSuccess, setEditSuccess] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState<string | null>(null);

  const fetchRoles = async (): Promise<void> => {
    setRolesLoading(true);
    setRolesError(null);
    try {
      const response = await fetch('/api/roles', { credentials: 'include' });
      if (response.status === 401) {
        window.dispatchEvent(new CustomEvent('auth-unauthorized'));
        return;
      }
      if (!response.ok) {
        throw new Error('Failed to load roles.');
      }
      const data = (await response.json()) as { roles: RoleRecord[] };
      const roleList = Array.isArray(data.roles) ? data.roles : [];
      setRoles(roleList);
      if (roleList.length > 0 && !selectedRoleName) {
        setSelectedRoleName(roleList[0].name);
        setEditPrompt(roleList[0].systemPrompt);
      }
    } catch (error) {
      setRolesError('Unable to load roles. Please refresh and try again.');
    } finally {
      setRolesLoading(false);
    }
  };

  useEffect(() => {
    void fetchRoles();
  }, []);

  useEffect(() => {
    const selected = roles.find((role) => role.name === selectedRoleName);
    if (selected) {
      setEditPrompt(selected.systemPrompt);
    }
  }, [roles, selectedRoleName]);

  const handleCreate = async (): Promise<void> => {
    setCreateError(null);
    setCreateSuccess(null);
    if (!createName.trim() || !createDescription.trim() || !createPrompt.trim()) {
      setCreateError('Role name, description and system prompt are required.');
      return;
    }
    try {
      const response = await fetch('/api/roles', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: createName.trim(),
          description: createDescription.trim(),
          systemPrompt: createPrompt.trim()
        })
      });
      if (response.status === 401) {
        window.dispatchEvent(new CustomEvent('auth-unauthorized'));
        return;
      }
      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        setCreateError(errorBody?.error || 'Unable to create role.');
        return;
      }
      setCreateSuccess('Role created.');
      setCreateName('');
      setCreatePrompt('');
      await fetchRoles();
    } catch (error) {
      setCreateError('Unable to create role.');
    }
  };

  const handleUpdate = async (): Promise<void> => {
    setEditError(null);
    setEditSuccess(null);
    if (!selectedRoleName.trim() || !editPrompt.trim()) {
      setEditError('Select a role and enter a system prompt.');
      return;
    }
    try {
      const response = await fetch(`/api/roles/${encodeURIComponent(selectedRoleName)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ systemPrompt: editPrompt.trim() })
      });
      if (response.status === 401) {
        window.dispatchEvent(new CustomEvent('auth-unauthorized'));
        return;
      }
      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        setEditError(errorBody?.error || 'Unable to update role.');
        return;
      }
      setEditSuccess('Role updated.');
      await fetchRoles();
    } catch (error) {
      setEditError('Unable to update role.');
    }
  };

  const handleDelete = async (): Promise<void> => {
    setDeleteError(null);
    setDeleteSuccess(null);
    if (!selectedRoleName.trim()) {
      setDeleteError('Select a role to delete.');
      return;
    }
    try {
      const response = await fetch(`/api/roles/${encodeURIComponent(selectedRoleName)}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.status === 401) {
        window.dispatchEvent(new CustomEvent('auth-unauthorized'));
        return;
      }
      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        setDeleteError(errorBody?.error || 'Unable to delete role.');
        return;
      }
      setDeleteSuccess('Role deleted.');
      setSelectedRoleName('');
      setEditPrompt('');
      await fetchRoles();
    } catch (error) {
      setDeleteError('Unable to delete role.');
    }
  };

  return (
    <Stack tokens={{ childrenGap: 24 }} styles={{ root: { padding: 32, maxWidth: 900, margin: '0 auto' } }}>
      <TopNav isAuthenticated={true} isAdmin={true} />
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
        <Stack>
          <Text variant="xLarge">Roles Admin</Text>
          {userEmail && <Text variant="small">Signed in as {userEmail}</Text>}
        </Stack>
      </Stack>

      <Stack tokens={{ childrenGap: 8 }}>
        <Text variant="large">Current roles</Text>
        {rolesLoading && <Text>Loading roles...</Text>}
        {rolesError && (
          <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
            {rolesError}
          </MessageBar>
        )}
        {!rolesLoading && roles.length === 0 && <Text>No roles found.</Text>}
        {!rolesLoading &&
          roles.map((role) => (
            <Stack
              key={role.id}
              tokens={{ childrenGap: 6 }}
              styles={{
                root: {
                  padding: 12,
                  border: '1px solid #e1dfdd',
                  borderRadius: 6
                }
              }}
            >
              <Text variant="mediumPlus">{role.name}</Text>
              <Text variant="small">{role.systemPrompt}</Text>
              <Text variant="small">Created: {new Date(role.createdAt).toLocaleString()}</Text>
            </Stack>
          ))}
      </Stack>

      <Stack tokens={{ childrenGap: 16 }}>
        <Text variant="large">Create role</Text>
        <TextField label="Role name" value={createName} onChange={(_, value) => setCreateName(value || '')} required />
        <TextField
          label="Role description"
          value={createDescription}
          onChange={(_, value) => setCreateDescription(value || '')}
          required
        />
        <TextField
          label="System prompt"
          multiline
          rows={4}
          value={createPrompt}
          onChange={(_, value) => setCreatePrompt(value || '')}
          required
        />
        {createError && (
          <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
            {createError}
          </MessageBar>
        )}
        {createSuccess && (
          <MessageBar messageBarType={MessageBarType.success} isMultiline={false}>
            {createSuccess}
          </MessageBar>
        )}
        <PrimaryButton onClick={handleCreate}>Create role</PrimaryButton>
      </Stack>

      <Stack tokens={{ childrenGap: 16 }}>
        <Text variant="large">Edit role</Text>
        <Dropdown
          label="Role"
          placeholder={rolesLoading ? 'Loading roles...' : 'Select a role'}
          options={roles.map((role) => ({ key: role.name, text: role.name }))}
          selectedKey={selectedRoleName || undefined}
          onChange={(_, option) => setSelectedRoleName(String(option?.key || ''))}
          disabled={rolesLoading || roles.length === 0}
        />
        <TextField
          label="System prompt"
          multiline
          rows={4}
          value={editPrompt}
          onChange={(_, value) => setEditPrompt(value || '')}
          required
        />
        {editError && (
          <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
            {editError}
          </MessageBar>
        )}
        {editSuccess && (
          <MessageBar messageBarType={MessageBarType.success} isMultiline={false}>
            {editSuccess}
          </MessageBar>
        )}
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <PrimaryButton onClick={handleUpdate}>Update role</PrimaryButton>
          <DefaultButton onClick={handleDelete}>Delete role</DefaultButton>
        </Stack>
        {deleteError && (
          <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
            {deleteError}
          </MessageBar>
        )}
        {deleteSuccess && (
          <MessageBar messageBarType={MessageBarType.success} isMultiline={false}>
            {deleteSuccess}
          </MessageBar>
        )}
      </Stack>
    </Stack>
  );
};

export default Roles;
